import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "reactstrap";
import brazilWorldCupBg from "../../../assets/images/brazil_worldcup_bg.png";
import neoLogo from "../../../assets/images/Neo_Logo_A.svg";
import { useTenant } from "../../../hooks/useTenant";
import { usePerformanceData } from "../../Management/Performance/hooks/usePerformanceData";
import ProfessorDetail from "./ProfessorDetail";
import WorldCupSticker from "./components/WorldCupSticker";
import WorldCupBracket from "./components/WorldCupBracket";
const getFunnyStats = (professor) => {
    if (!professor) return [];

    // Usar uma soma simples dos caracteres do ID/Nome para gerar um "seed" determinístico
    const seed = (professor.idEmployee || professor.employeeName || "")
        .split("")
        .reduce((acc, char) => acc + char.charCodeAt(0), 0);

    const golsContraOptions = ["3", "5", "Nenhum (por milagre)", "2 (sendo 1 de calcanhar)", "1 (no próprio ângulo)", "4 (recordista)"];
    const chutesForaOptions = ["300", "150", "Incontáveis", "420", "Todos que tentou", "99% dos chutes"];
    const chutesNoGolOptions = ["-6", "-2", "Nenhum", "1 (por engano)", "-10", "Quase 1", "0.5 (bola murcha)"];
    const passesCorretosOptions = ["Sem dados", "1 (recuo pro goleiro)", "0", "Apenas por sorte", "Melhor não comentar", "Nenhum (foi pro rival)"];
    const passesErradosOptions = ["Incontáveis", "Todos os outros", "99.9%", "Uma infinidade", "Perdeu a conta no 50º", "100% de aproveitamento reverso"];
    const tomadaBolaOptions = [
        "3, do próprio colega de time",
        "1, do bandeirinha",
        "0 (evita contato físico)",
        "4 (derrubando o técnico)",
        "2 (pedindo desculpas)",
        "Nenhuma (corre da bola)"
    ];
    const penaltisOptions = [
        "Cometeu 3",
        "Cometeu 5 (sendo 2 simulados)",
        "Cometeu 2 (um com a mão)",
        "Cavou 4 no treino",
        "Melhor não comentar",
        "Cometeu 1 logo no aquecimento"
    ];
    const adicionaisOptions = [
        { label: "Cartões Amarelos", val: "3 por reclamação", icon: "mdi-card", color: "#f1b44c" },
        { label: "Cartões Vermelhos", val: "2 (sendo 1 no banco)", icon: "mdi-card-outline", color: "#f46a6a" },
        { label: "Corrida em Campo", val: "Só correu pro bebedouro", icon: "mdi-run", color: "#34c38f" },
        { label: "Dribles Tentados", val: "Se driblou sozinho 4 vezes", icon: "mdi-navigation", color: "#FFE066" },
        { label: "Condicionamento", val: "Cansou no par ou ímpar", icon: "mdi-heart-broken", color: "#f46a6a" }
    ];

    // Funções modulares determinísticas baseadas no seed
    const golsContra = golsContraOptions[seed % golsContraOptions.length];
    const chutesFora = chutesForaOptions[(seed + 1) % chutesForaOptions.length];
    const chutesNoGol = chutesNoGolOptions[(seed + 2) % chutesNoGolOptions.length];
    const passesCorretos = passesCorretosOptions[(seed + 3) % passesCorretosOptions.length];
    const passesErrados = passesErradosOptions[(seed + 4) % passesErradosOptions.length];
    const tomadaBola = tomadaBolaOptions[(seed + 5) % tomadaBolaOptions.length];
    const penaltis = penaltisOptions[(seed + 6) % penaltisOptions.length];
    const adicional = adicionaisOptions[(seed + 7) % adicionaisOptions.length];

    return [
        { label: "Gols Contra", val: golsContra, icon: "mdi-soccer", color: "#f46a6a" },
        { label: "Chutes Fora", val: chutesFora, icon: "mdi-chevron-double-up", color: "#f1b44c" },
        { label: "Chutes no Gol", val: chutesNoGol, icon: "mdi-target", color: "#f46a6a" },
        { label: "Passes Corretos", val: passesCorretos, icon: "mdi-check-circle-outline", color: "rgba(255,255,255,0.4)" },
        { label: "Passes Errados", val: passesErrados, icon: "mdi-close-circle-outline", color: "#f46a6a" },
        { label: "Tomada de Bola", val: tomadaBola, icon: "mdi-hand-back-left", color: "#FFE066" },
        { label: "Pênaltis", val: penaltis, icon: "mdi-whistle", color: "#f46a6a" },
        adicional
    ];
};

const KioskProfessors = () => {
    document.title = "Ranking de Professores | NeoApp";
    const navigate = useNavigate();

    const [bgLoaded, setBgLoaded] = useState(false);
    useEffect(() => {
        const img = new Image();
        img.src = brazilWorldCupBg;
        img.onload = () => setBgLoaded(true);
        img.onerror = () => setBgLoaded(true);
    }, []);

    const { idTenant, loading: tenantLoading } = useTenant();

    const currentPeriod = React.useMemo(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    }, []);

    const { snapshots, loading: perfLoading, recalculating } = usePerformanceData({
        idTenant,
        selectedPeriod: currentPeriod
    });

    const [selectedProfessor, setSelectedProfessor] = useState(null);
    const [previewProfessor, setPreviewProfessor] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const sidebarRef = useRef(null);

    // Efeito para fechar o painel lateral ao clicar fora dele
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isSidebarCollapsed && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                setIsSidebarCollapsed(true);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isSidebarCollapsed]);

    const handleSelectPlayer = (player) => {
        setPreviewProfessor(player);
        setIsSidebarCollapsed(false);
    };


    const collaborators = React.useMemo(() => {
        const excludedRoleIds = ["owner", "admin", "super_admin", "manager", "gestor"];
        const excludedRoleNames = ["Gestor", "Gerente", "Administrador", "Diretor", "Owner"];

        return snapshots
            .filter(s =>
                !excludedRoleIds.includes(String(s.employeeRole).toLowerCase()) &&
                !excludedRoleNames.includes(s.employeeRoleName)
            )
            .sort((a, b) => {
                if (b.scoreFinal !== a.scoreFinal) {
                    return b.scoreFinal - a.scoreFinal;
                }
                const membersA = a.metrics?.raw?.totalEnrolledCount || 0;
                const membersB = b.metrics?.raw?.totalEnrolledCount || 0;
                return membersB - membersA;
            });
    }, [snapshots]);

    // Inicializa o preview com o campeão (1º colocado)
    useEffect(() => {
        if (collaborators.length > 0 && !previewProfessor) {
            setPreviewProfessor(collaborators[0]);
        }
    }, [collaborators, previewProfessor]);

    const loading = tenantLoading || perfLoading || recalculating || !bgLoaded;

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", background: "linear-gradient(135deg, #051408 0%, #0d2a13 50%, #030f06 100%)" }}>
                <div className="text-center">
                    <Spinner color="light" style={{ width: "3rem", height: "3rem" }} />
                    <p className="text-white-50 mt-3 fw-medium font-size-16">Carregando ranking...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: "100vh",
            position: "relative",
            background: "linear-gradient(135deg, #051408 0%, #0d2a13 50%, #030f06 100%)",
            overflowX: "hidden",
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none"
        }}>
            {/* Overlay com textura sutil */}
            <div style={{
                position: "fixed",
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundImage: `url(${brazilWorldCupBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.22,
                zIndex: 0
            }} />

            <div style={{ zIndex: 1, position: "relative" }}>
                {selectedProfessor ? (
                    <ProfessorDetail
                        professor={selectedProfessor}
                        onBack={() => setSelectedProfessor(null)}
                    />
                ) : (
                    <>
                        {/* ═══ Header ═══ */}
                        <div className="px-3 pt-4 pb-3 text-center position-relative" style={{ zIndex: 50 }}>
                            <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                                <img src={neoLogo} alt="NeoApp" style={{ height: "26px", filter: "brightness(10)" }} />
                                <h4 className="mb-0 fw-bold text-white mt-1" style={{ letterSpacing: "-0.5px" }}>
                                    Ranking da Equipe
                                </h4>
                            </div>
                        </div>

                        {/* ═══ Split-Screen Layout (Chaveamento na Esquerda, Preview na Direita) ═══ */}
                        <div
                            className="d-flex flex-row gap-4 px-3 align-items-stretch"
                            style={{
                                height: "calc(100vh - 130px)",
                                overflow: "hidden",
                                maxWidth: "1400px",
                                margin: "0 auto"
                            }}
                        >
                            {/* Coluna da Esquerda: Chaveamento com Rolagem Sem Corte */}
                            <div
                                className="custom-scrollbar px-3 pb-5"
                                style={{
                                    flex: "1 1 0",
                                    overflow: "auto",
                                    height: "100%",
                                    paddingTop: "20px",
                                    display: "block" // Bloco para evitar o flex centering que cortava o topo
                                }}
                            >
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100%" }}>
                                    <WorldCupBracket
                                        collaborators={collaborators}
                                        onSelectPlayer={handleSelectPlayer}
                                    />
                                </div>
                            </div>

                            {/* Coluna da Direita: Card de Preview Ampliado com Fechamento/Setinha */}
                            {previewProfessor && !isSidebarCollapsed && (
                                <div
                                    ref={sidebarRef}
                                    className="d-flex flex-column align-items-center justify-content-start flex-shrink-0 mb-4 mb-md-0 custom-scrollbar"
                                    style={{
                                        width: "clamp(280px, 35vw, 360px)",
                                        background: "rgba(5, 20, 8, 0.65)",
                                        backdropFilter: "blur(15px)",
                                        border: "1px solid rgba(255, 255, 255, 0.12)",
                                        borderRadius: "20px",
                                        padding: "20px",
                                        boxShadow: "-10px 0 30px rgba(0,0,0,0.5)",
                                        maxHeight: "95%",
                                        height: "auto",
                                        overflowY: "auto",
                                        marginTop: "10px",
                                        zIndex: 10,
                                        alignSelf: "center",
                                        position: "relative",
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    {/* Setinha para Recolher no Topo Direito */}
                                    <div className="d-flex align-items-center justify-content-end w-100 mb-3">
                                        <button
                                            onClick={() => setIsSidebarCollapsed(true)}
                                            className="btn p-0 d-flex align-items-center justify-content-center border-0 text-white-50"
                                            style={{
                                                width: "44px",
                                                height: "44px",
                                                background: "rgba(255,255,255,0.06)",
                                                borderRadius: "50%",
                                                transition: "all 0.2s"
                                            }}
                                            onMouseOver={e => e.currentTarget.style.color = "#FFE066"}
                                            onMouseOut={e => e.currentTarget.style.color = "rgba(255,255,255,0.5)"}
                                            title="Recolher painel"
                                        >
                                            <i className="mdi mdi-chevron-right font-size-24"></i>
                                        </button>
                                    </div>

                                    <div className="d-flex flex-column align-items-center text-center w-100">
                                        {/* Card Clicável que abre o Relatório de Desempenho */}
                                        <div
                                            onClick={() => setSelectedProfessor(previewProfessor)}
                                            style={{
                                                cursor: "pointer",
                                                transition: "all 0.25s ease",
                                                borderRadius: "12px"
                                            }}
                                            onMouseOver={e => e.currentTarget.style.transform = "scale(1.04)"}
                                            onMouseOut={e => e.currentTarget.style.transform = "scale(1)"}
                                            title="Clique para abrir o relatório de desempenho completo"
                                        >
                                            <WorldCupSticker
                                                professor={previewProfessor}
                                                rank={collaborators.findIndex(c => c.idEmployee === previewProfessor.idEmployee) + 1}
                                                size="large"
                                                style={{
                                                    width: "clamp(140px, 18vh, 180px)",
                                                    height: "clamp(186px, 24vh, 240px)",
                                                    transform: "none",
                                                    boxShadow: "0 15px 35px rgba(0,0,0,0.6)"
                                                }}
                                            />
                                        </div>

                                        <span
                                            className="badge rounded-pill px-3 py-1 font-size-11 fw-bold text-uppercase mt-3 mb-2"
                                            style={{
                                                background: "rgba(255, 224, 102, 0.15)",
                                                color: "#FFE066",
                                                border: "1px solid rgba(255, 224, 102, 0.3)",
                                                letterSpacing: "0.5px"
                                            }}
                                        >
                                            Score: {Math.round(previewProfessor.scoreFinal)}%
                                        </span>

                                        {/* ⚽ ESTATÍSTICAS ENGRAÇADAS DO JOGADOR NA LATERAL */}
                                        <div 
                                            className="custom-scrollbar w-100 mt-2 mb-3 px-2 py-2" 
                                            style={{ 
                                                maxHeight: "185px", 
                                                overflowY: "auto",
                                                background: "rgba(255,255,255,0.02)",
                                                border: "1px solid rgba(255,255,255,0.05)",
                                                borderRadius: "12px"
                                            }}
                                        >
                                            <div 
                                                className="text-start font-size-9 fw-black text-warning mb-2 text-uppercase text-center" 
                                                style={{ letterSpacing: "1.2px", opacity: 0.85 }}
                                            >
                                                ⚽ DESEMPENHO NA COPA
                                            </div>
                                            <div className="d-flex flex-column gap-2">
                                                {getFunnyStats(previewProfessor).map((stat, idx) => (
                                                    <div key={idx} className="d-flex align-items-center justify-content-between border-bottom pb-1 gap-2" style={{ borderColor: "rgba(255,255,255,0.03)", minHeight: "22px", fontSize: "9px" }}>
                                                        <div className="d-flex align-items-center gap-1 text-white-50 flex-shrink-0">
                                                            <i className={`mdi ${stat.icon}`} style={{ color: stat.color, fontSize: "11px" }}></i>
                                                            <span style={{ whiteSpace: "nowrap" }}>{stat.label}</span>
                                                        </div>
                                                        <span className="fw-bold text-end" style={{ color: stat.color, wordBreak: "break-word", fontSize: "9px" }}>{stat.val}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Legenda de auxílio interativo */}
                                        <span className="font-size-10 text-white-50 mt-1 opacity-75 fw-medium">
                                            💡 Clique na figurinha para ver o relatório completo
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Setinha flutuante para Expandir o Painel (quando recolhido) */}
                            {isSidebarCollapsed && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsSidebarCollapsed(false);
                                    }}
                                    className="btn position-fixed rounded-circle d-flex align-items-center justify-content-center shadow-lg border-0"
                                    style={{
                                        top: "50%",
                                        right: "20px",
                                        transform: "translateY(-50%)",
                                        width: "44px",
                                        height: "44px",
                                        background: "rgba(5, 20, 8, 0.8)",
                                        backdropFilter: "blur(10px)",
                                        border: "1px solid rgba(255,255,255,0.15)",
                                        color: "#FFE066",
                                        boxShadow: "0 4px 15px rgba(0,0,0,0.5)",
                                        zIndex: 100,
                                        transition: "all 0.2s"
                                    }}
                                    onMouseOver={e => e.currentTarget.style.transform = "translateY(-50%) scale(1.08)"}
                                    onMouseOut={e => e.currentTarget.style.transform = "translateY(-50%) scale(1)"}
                                    title="Mostrar painel de detalhes"
                                >
                                    <i className="mdi mdi-chevron-left font-size-24"></i>
                                </button>
                            )}
                        </div>

                        {/* Voltar */}
                        <div className="position-fixed" style={{ bottom: '20px', left: '20px', zIndex: 10 }}>
                            <button
                                onClick={() => navigate("/kiosk")}
                                className="btn rounded-circle d-flex align-items-center justify-content-center shadow-lg border-0"
                                style={{
                                    width: "52px", height: "52px",
                                    background: "rgba(255,255,255,0.08)",
                                    backdropFilter: "blur(10px)",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    transition: "all 0.2s"
                                }}
                                title="Voltar"
                            >
                                <i className="mdi mdi-arrow-left text-white font-size-24"></i>
                            </button>
                        </div>
                    </>
                )}

                {/* Powered By */}
                <div className="position-fixed" style={{ bottom: '20px', right: '20px', zIndex: 10 }}>
                    <div className="d-flex align-items-center gap-2 opacity-25">
                        <span style={{ fontSize: "9px", fontWeight: "700", color: "#fff", letterSpacing: "1px" }}>POWERED BY</span>
                        <img src={neoLogo} alt="NeoApp" style={{ height: "12px", filter: "brightness(10)" }} />
                    </div>
                </div>

            </div>
        </div>
    );
};

export default KioskProfessors;



